//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { DateUtils } from 'unison';

import * as metricsApi from '@apis/metrics/metricsApi';
import { getExportToEmail } from '@apis/metrics/metricsApi';
import {
  IStatsExportRequest,
  InsightsSourceType,
  UnifiedMetricsMetadataResponseWithCurrencyOverride,
} from '@apis/metrics/metricsApiModel';
import * as uiaApi from '@apis/universal-image-actions/universalImageActionsApi';
import * as userSettingsApi from '@apis/user-settings/userSettingsApi';
import {
  IInsightsMetadataState,
  IInsightsStateAndParams,
  InsightsRefreshSettings,
} from '@insights/redux/metadata/insightsMetadataModel';
import { getInsightsMetadataFlags } from '@insights/redux/selectors/getInsightsMetadata';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import { EcommerceProvider } from '@models/MetricsGatewayApiModels';
import { IUnisonDateRange } from '@models/UnisonTypes';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';
import { getUserEmail } from '@redux/selectors/getSessionInfo';
import { getRequestErrorMessage } from '@utils/getRequestErrorMessage';

const initialState: IInsightsMetadataState = {
  dateRange: DateUtils.getDateRangeFromAlias('last7Days'),
  comparisonDateRange: undefined,
  currentStateAndParams: undefined,
  currentGallery: undefined,
  ecommerceProvider: undefined,
  ecommerceTrackingEnabled: false,
  validTouchpoints: [],
  activationStatuses: undefined,
  currency: undefined,
  configuredTrackingParamGroups: undefined,
  optedOutSettings: {
    isInsightsRefreshOptedOut: undefined,
    isInsightsRefreshOptOutBannerDismissed: undefined,
  },
  flags: {
    hasFetchedMetadata: false,
    isFetchingInsightsRefreshSettings: false,
    isUpdatingInsightsRefreshSettings: false,
    isSendingExportEmail: false,
  },
  errors: {
    fetchMetadataError: undefined,
    fetchInsightsRefreshSettingsError: undefined,
    updateInsightsRefreshSettingsError: undefined,
    exportEmailError: undefined,
  },
};

const { actions, reducer } = createSlice({
  name: SliceNames.InsightsMetadata,
  initialState,
  reducers: {
    updateDateRange(state, action: PayloadAction<IUnisonDateRange>) {
      state.dateRange = action.payload;
    },
    updateComparisonDateRange(state, action: PayloadAction<IUnisonDateRange>) {
      state.comparisonDateRange = action.payload;
    },
    updateCurrentStateAndParams(
      state,
      action: PayloadAction<IInsightsStateAndParams>,
    ) {
      state.currentStateAndParams = action.payload;
    },
    updateCurrentGallery(state, action: PayloadAction<string>) {
      state.currentGallery = action.payload;
    },
    updateEcommerceProvider(state, action: PayloadAction<EcommerceProvider>) {
      state.ecommerceProvider = action.payload;
    },
    setEcommerceTrackingEnabled(state) {
      state.ecommerceTrackingEnabled = true;
    },
    setEcommerceTrackingDisabled(state) {
      state.ecommerceTrackingEnabled = false;
    },
    setValidTouchpoints(state, action: PayloadAction<InsightsSourceType[]>) {
      state.validTouchpoints = action.payload;
    },
    setCurrency(state, action: PayloadAction<string>) {
      state.currency = action.payload;
    },
    requestMetadata(state) {
      state.errors.fetchMetadataError = undefined;
    },
    recieveMetadataSucceeded(
      state,
      action: PayloadAction<UnifiedMetricsMetadataResponseWithCurrencyOverride>,
    ) {
      state.flags.hasFetchedMetadata = true;

      const metadata = action.payload;
      state.ecommerceProvider = metadata.ecommerceProvider as EcommerceProvider;
      state.ecommerceTrackingEnabled = metadata.ecommerceTracking;
      state.validTouchpoints =
        metadata.validTouchpoints as InsightsSourceType[];
      state.activationStatuses = metadata.activationStatuses;
      state.createdOn = metadata.createdOn;
      state.timezone = metadata.timezone;
      state.currency = metadata.currency;
      state.configuredTrackingParamGroups =
        metadata.configuredTrackingParamGroups;
    },
    recieveMetadataFailed(state, action: PayloadAction<string>) {
      state.flags.hasFetchedMetadata = true;
      state.errors.fetchMetadataError = action.payload;
    },
    resetMetadataState(state) {
      state = initialState;
    },
    requestInsightsRefreshSettings(state) {
      state.errors.fetchInsightsRefreshSettingsError = undefined;
      state.flags.isFetchingInsightsRefreshSettings = true;
    },
    receiveInsightsRefreshSettingsSucceeded(
      state,
      action: PayloadAction<InsightsRefreshSettings>,
    ) {
      state.optedOutSettings = action.payload;
      state.flags.isFetchingInsightsRefreshSettings = false;
    },
    receiveInsightsRefreshSettingsFailed(state, action: PayloadAction<string>) {
      state.errors.fetchInsightsRefreshSettingsError = action.payload;
      state.flags.isFetchingInsightsRefreshSettings = false;
    },
    requestUpdateInsightsRefreshSettings(state) {
      state.errors.updateInsightsRefreshSettingsError = undefined;
      state.flags.isUpdatingInsightsRefreshSettings = true;
    },
    receiveUpdateInsightsRefreshSettingsSucceeded(
      state,
      action: PayloadAction<InsightsRefreshSettings>,
    ) {
      state.optedOutSettings = action.payload;
      state.flags.isUpdatingInsightsRefreshSettings = false;
    },
    receiveUpdateInsightsRefreshSettingsFailed(
      state,
      action: PayloadAction<string>,
    ) {
      state.errors.updateInsightsRefreshSettingsError = action.payload;
      state.flags.isUpdatingInsightsRefreshSettings = false;
    },
    requestSendExportEmail(state) {
      state.errors.exportEmailError = undefined;
      state.flags.isSendingExportEmail = true;
    },
    receiveSendExportEmailSucceeded(state) {
      state.flags.isSendingExportEmail = false;
    },
    receiveSendExportEmailFailed(state, action: PayloadAction<string>) {
      state.errors.exportEmailError = action.payload;
      state.flags.isSendingExportEmail = false;
    },
  },
});

export const setCurrentStateAndParams = createThunk(
  (currentStateAndParams: IInsightsStateAndParams) => async (dispatch) => {
    dispatch(actions.updateCurrentStateAndParams(currentStateAndParams));
  },
);

export const getMetadata = createThunk(() => async (dispatch, getState) => {
  dispatch(actions.requestMetadata());
  try {
    const metadataResponse = await metricsApi.getMetadata();
    const trackingParamsResponse = await uiaApi.getCurrentTrackingParams();

    // By default we use the last 7 days date range but this is invalid for new clients so set the default to last 24 hours in those cases
    const dateRange = getState().insights.metadata.dateRange;
    if (
      metadataResponse.createdOn &&
      (dateRange.startDate.getTime() < metadataResponse.createdOn ||
        dateRange.endDate.getTime() < metadataResponse.createdOn)
    ) {
      dispatch(
        actions.updateDateRange(DateUtils.getDateRangeFromAlias('last24Hours')),
      );
    }

    const metatdataWithTrackingParams = {
      ...metadataResponse,
      configuredTrackingParamGroups:
        trackingParamsResponse.trackingParams?.paramGroups,
    };
    dispatch(actions.recieveMetadataSucceeded(metatdataWithTrackingParams));
  } catch (e) {
    const error = getRequestErrorMessage(e);
    dispatch(
      actions.recieveMetadataFailed(error ?? 'Failed to fetch metadata'),
    );
  }
});

export const fetchInsightsRefreshSettings = createThunk(
  () => async (dispatch) => {
    dispatch(actions.requestInsightsRefreshSettings());
    try {
      const {
        isInsightsRefreshOptOutBannerDismissed = false,
        isInsightsRefreshOptedOut = false,
      } = await userSettingsApi.getUserSettings();

      dispatch(
        actions.receiveInsightsRefreshSettingsSucceeded({
          isInsightsRefreshOptedOut,
          isInsightsRefreshOptOutBannerDismissed,
        }),
      );
    } catch (_e) {
      dispatch(
        actions.receiveInsightsRefreshSettingsFailed(
          'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
        ),
      );
    }
  },
);

export const setInsightsRefreshSettings = createThunk(
  (settings: Partial<InsightsRefreshSettings>) => async (dispatch) => {
    dispatch(actions.requestUpdateInsightsRefreshSettings());
    try {
      await userSettingsApi.setUserSettings(settings);
      dispatch(actions.receiveUpdateInsightsRefreshSettingsSucceeded(settings));
    } catch (_e) {
      dispatch(
        actions.receiveUpdateInsightsRefreshSettingsFailed(
          'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
        ),
      );
    }
  },
);

export const sendExportEmail = createThunk(
  (statsRequestBase: IStatsExportRequest) => async (dispatch, getState) => {
    const state = getState();
    const userEmail = getUserEmail(state);
    const { isSendingExportEmail } = getInsightsMetadataFlags(state);

    // if a request to generate the report and email is already in progress we don't want to send another request
    if (isSendingExportEmail) {
      return;
    }

    dispatch(actions.requestSendExportEmail());

    try {
      await getExportToEmail(statsRequestBase);

      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Exported Insights',
          type: ToastType.SUCCESS,
          message: `${userEmail} will receive your file download. It may take several minutes to receive the email.`,
          options: {
            toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
          },
        }),
      );

      dispatch(actions.receiveSendExportEmailSucceeded());
    } catch (e) {
      dispatch(
        actions.receiveSendExportEmailFailed(
          getRequestErrorMessage(e) ??
            '[sendExportEmail] Failed to send export email',
        ),
      );

      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Export Error',
          type: ToastType.ERROR,
          message:
            'An unknown error occurred while generating your CSV file. Please try again or contact Bazaarvoice Support.',
          options: {
            toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
          },
        }),
      );
    }
  },
);

export const {
  resetMetadataState,
  setCurrency,
  setEcommerceTrackingDisabled,
  setEcommerceTrackingEnabled,
  setValidTouchpoints,
  updateComparisonDateRange,
  updateCurrentGallery,
  updateDateRange,
  updateEcommerceProvider,
} = actions;

export default reducer;
